import { useEffect, useState, useRef } from "react";
import { data, useParams } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { DateTime } from "luxon";
import {
  QuoteWSUpdate,
  PublicQuote,
  QuoteUnit,
  QuoteStatus,
  WSUpdate,
} from "@/types/quotes";
import { publicQuotesAPI } from "@/services/api";
import { websocketAPI } from "@/services/websocket";
import { getFormattedTimeUntilSp } from "@/lib/utils";
import { handleApiError, showSuccessToast } from "@/lib/error-handler";

// Import modularized components
import { ActiveQuoteComponent } from "@/components/accept-quote/ActiveQuote";
import { AcceptedQuoteComponent } from "@/components/accept-quote/AcceptedQuote";
import { ExpiredQuoteComponent } from "@/components/accept-quote/ExpiredQuote";

import { Card, CardContent } from "@/components/ui/shadcn/card";
import { AlertCircle, AlertTriangle } from "lucide-react";

import { RefreshCw } from "lucide-react";

export function LoadingComponent() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  );
}

interface ErrorComponentProps {
  errorMessage: string;
  notFound?: boolean;
}

export function ErrorComponent({
  errorMessage,
  notFound = false,
}: ErrorComponentProps) {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-slate-950 to-gray-900 p-6">
      <Card className="w-full max-w-lg bg-red-900/40 backdrop-blur-md shadow-xl border border-red-500/30 rounded-2xl">
        <CardContent className="pt-6 pb-4">
          <div className="flex flex-col items-center text-center p-6">
            {notFound ? (
              <AlertTriangle className="h-16 w-16 text-red-400 mb-4" />
            ) : (
              <AlertCircle className="h-16 w-16 text-red-400 mb-4" />
            )}
            <h2 className="text-2xl font-bold text-white mb-2">
              {notFound ? "Cotización No Encontrada" : "Error"}
            </h2>
            <p className="text-gray-300">
              {notFound
                ? "Esta cotización puede haber expirado o sido eliminada."
                : errorMessage}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export function AcceptQuotePage() {
  const { slug } = useParams();
  const { toast } = useToast();
  const [quote, setQuote] = useState<PublicQuote | null>(null);
  const [quotePrices, setQuotePrices] = useState<QuoteWSUpdate | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepted, setAccepted] = useState(false);
  const [expired, setExpired] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetCounter, setResetCounter] = useState(0);
  const [timeLeft, setTimeLeft] = useState<number | null>(null); // Refresh countdown
  const [timeUntilExpiry, setTimeUntilExpiry] = useState<string>(""); // Expiration countdown

  // WebSocket related state
  const socketRef = useRef<WebSocket | null>(null);
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(true);

  /** Close WebSocket connection */
  const closeWebSocket = () => {
    if (socketRef.current) {
      console.log("🔌 Closing WebSocket connection");
      socketRef.current.close();
      socketRef.current = null;
      setIsSocketConnected(false);
    }
  };

  /** Handle price update messages */
  const handlePriceUpdate = (data: QuoteWSUpdate) => {
    if (!quote) return;

    setQuotePrices({
      base: data.base,
      counter: data.counter,
      amount: quote.amount,
      unit: quote.unit,
      status: data.status,
      price: data.price,
      total_from: data.total_from,
      total_to: data.total_to,
      price_fetched_at: data.price_fetched_at,
      price_expires_at: data.price_expires_at,
      price_ttl_ms: data.price_ttl_ms,
      quote_expiration: data.quote_expiration,
    });

    const seconds_remaining = parseInt(data.price_ttl_ms) / 1000;
    console.log("🔄 seconds_remaining:", seconds_remaining);
    if (seconds_remaining - 0.2 <= 0) {
      setTimeLeft(0);
      setResetCounter(0);
      setIsRefreshing(true);
    } else {
      setTimeLeft(seconds_remaining);
      setResetCounter(prev => prev + 1);
      setIsRefreshing(false);
    }

    setError(null);
  };

  /** Handle status change messages */
  const handleStatusChange = async (status: QuoteStatus) => {
    try {
      const updatedQuote = await publicQuotesAPI.getQuoteBySlug(slug!);
      setQuote(updatedQuote);

      if (updatedQuote.status === QuoteStatus.ACCEPTED || updatedQuote.status === QuoteStatus.EXECUTED) {
        setAccepted(true);
        closeWebSocket();
      } else if (updatedQuote.status === QuoteStatus.EXPIRED) {
        setExpired(true);
        closeWebSocket();
      }
    } catch (error) {
      console.error("❌ Error refreshing quote after status change:", error);
      // Even if the refresh fails, update the UI based on the status message
      if (status === QuoteStatus.ACCEPTED) {
        setAccepted(true);
        closeWebSocket();
      } else if (status === QuoteStatus.EXPIRED) {
        setExpired(true);
        closeWebSocket();
      }
    }
  };

  /** Connect to WebSocket */
  const connectWebSocket = () => {
    if (!slug || accepted || expired) {
      return;
    }

    // Close existing connection if any
    closeWebSocket();

    try {
      const wsUrl = websocketAPI.getWebSocketUrl(`quotes/${slug}`);
      console.log("🔌 Connecting to WebSocket:", wsUrl);

      const socket = websocketAPI.connect(wsUrl, {
        onOpen: () => {
          console.log("🔌 WebSocket connection established");
          setIsSocketConnected(true);
          setError(null);
          setIsRefreshing(true);
        },

        onMessage: (event: MessageEvent) => {
          try {
            const ws_data = JSON.parse(event.data) as WSUpdate;
            console.log("🔔 WebSocket message received:", ws_data);

            let data: QuoteWSUpdate | undefined;
            let status: QuoteStatus | undefined;

            switch (ws_data.type) {
              case "price_update":
                if (!quote) return;
                data = ws_data.data as QuoteWSUpdate;
                if (!data) {
                  setIsRefreshing(true);
                  return;
                }
                handlePriceUpdate(data);
                break;
              case "status_change":
                status = ws_data.data?.status as QuoteStatus;
                console.log(`🔔 Quote status changed to: ${status}`);
                handleStatusChange(status);
                break;
            }
          } catch (error) {
            console.error("❌ Error processing WebSocket message:", error);
          }
        },

        onClose: (event: CloseEvent) => {
          console.log("🔌 WebSocket connection closed:", event.code, event.reason);
          setIsSocketConnected(false);

          if (event.code === 1000) {
            console.log("WebSocket closed normally");
          } else {
            setError("Conexión en tiempo real perdida. Intentando reconectar...");
          }
        },

        onError: () => {
          setError("Error en la conexión en tiempo real. Los precios podrían no estar actualizados.");
        },

        onReconnect: () => {
          setError("Intentando restablecer la conexión...");
        },

        maxReconnectAttempts: 5,
        reconnectInterval: 3000,
      });

      socketRef.current = socket;
    } catch (error) {
      console.error("❌ Error setting up WebSocket:", error);
      setError("No se pudo establecer la conexión en tiempo real. Los precios podrían no estar actualizados.");
    }
  };

  /** Handle successful quote acceptance */
  const handleQuoteAccepted = async () => {
    try {
      showSuccessToast(toast, "Éxito", "¡Cotización aceptada exitosamente!");

      // Close WebSocket as we don't need updates anymore
      // closeWebSocket();

      // Refresh quote after acceptance
      setLoading(true);
      const updatedQuote = await publicQuotesAPI.getQuoteBySlug(slug!);
      setQuote(updatedQuote);
      setAccepted(true);

      if (import.meta.env.DEV) {
        console.log("Quote accepted successfully:", updatedQuote);
      }
    } catch (error) {
      handleApiError("Refresh Quote", error, toast);
    } finally {
      setLoading(false);
    }
  };

  /** Handle quote acceptance error */
  const handleQuoteAcceptError = (errorMessage: string) => {
    setError(errorMessage);
    toast({
      title: "Error",
      description: errorMessage,
      variant: "error",
    });
  };

  /** Fetch the initial quote */
  useEffect(() => {
    const fetchQuote = async () => {
      try {
        if (!slug) {
          setError("Referencia de cotización inválida");
          setLoading(false);
          return;
        }

        const data = await publicQuotesAPI.getQuoteBySlug(slug);
        setQuote(data);

        // Check if quote is already accepted or expired
        if (data.status === QuoteStatus.ACCEPTED) {
          setAccepted(true);
          setLoading(false);
        } else if (data.status === QuoteStatus.EXPIRED) {
          setExpired(true);
          setLoading(false);
        } else {
          const now = DateTime.utc();
          const expiration = DateTime.fromISO(data.expiration, { zone: "utc" });

          // Check if the quote is expired by time
          if (now > expiration) {
            setExpired(true);
            setLoading(false);
            return;
          }

          // Set time remaining until expiration
          setTimeUntilExpiry(getFormattedTimeUntilSp(now, expiration));

          setLoading(false);
        }
      } catch (error) {
        handleApiError("Fetch Quote", error, toast);
        console.error("❌ Error fetching quote:", error);
        setError(
          "No se pudo cargar los detalles de la cotización. Puede haber expirado o sido eliminada."
        );
        setLoading(false);
      }
    };

    if (slug) {
      fetchQuote();
    }
  }, [slug]);

  /** Handle WebSocket connection */
  useEffect(() => {
    if (!slug || accepted || expired || loading) {
      return;
    }

    // Connect to WebSocket for real-time updates
    connectWebSocket();

    // Clean up WebSocket on unmount or when quote becomes accepted/expired
    return () => {
      closeWebSocket();
    };
  }, [slug, accepted, expired, loading]);

  /** Update countdown timer for animation */
  useEffect(() => {
    if (!timeLeft || accepted || expired) return;
    console.log("🔄 timeLeft:", timeLeft);
    if (timeLeft < 1) {
      setTimeLeft(0);
      setResetCounter((prev) => 0);
      setIsRefreshing(true);
    }

    const interval = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime === null || prevTime <= 1) {
          clearInterval(interval);
          setIsRefreshing(true);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [timeLeft, resetCounter, accepted, expired]);

  /** Update expiration countdown */
  useEffect(() => {
    if (!quote || accepted || expired) return;

    const updateExpirationCountdown = () => {
      const now = DateTime.utc();
      const expiration = DateTime.fromISO(quote.expiration, { zone: "utc" });

      // Check if quote has expired during the countdown
      if (now > expiration) {
        setExpired(true);
        // closeWebSocket();
        return;
      }

      setTimeUntilExpiry(getFormattedTimeUntilSp(now, expiration));
    };

    updateExpirationCountdown();
    const expirationInterval = setInterval(updateExpirationCountdown, 1000);
    return () => clearInterval(expirationInterval);
  }, [quote, accepted, expired]);

  /** Render the appropriate component based on the current state */
  if (loading) {
    return <LoadingComponent />;
  }

  if (error && !quote) {
    return <ErrorComponent errorMessage={error} />;
  }

  if (!quote) {
    return <ErrorComponent errorMessage="" notFound={true} />;
  }

  if (expired) {
    return <ExpiredQuoteComponent quote={quote} quotePrices={quotePrices} />;
  }

  if (accepted) {
    return <AcceptedQuoteComponent quote={quote} />;
  }

  return (
    <ActiveQuoteComponent
      quote={quote}
      quotePrices={quotePrices}
      timeLeft={timeLeft}
      timeRemaining={timeUntilExpiry}
      resetCounter={resetCounter}
      error={error}
      onAccept={handleQuoteAccepted}
      onAcceptError={handleQuoteAcceptError}
      slug={slug!}
      isSocketConnected={isSocketConnected}
      isRefreshing={isRefreshing}
    />
  );
}
